/**
 * Growthly Founder Story & 3D Interactive Avatar Card Component
 * Multi-Layer 3D Tilt, Specular Glare Tracking & Z-Depth Parallax
 */

class FounderStory3DCard {
  constructor(sectionElement) {
    this.section = sectionElement;
    this.id = this.section.dataset.sectionId;
    this.isRTL = this.section.getAttribute('dir') === 'rtl' || document.documentElement.getAttribute('dir') === 'rtl';

    this.cardStage = this.section.querySelector('[data-3d-stage]');
    this.card = this.section.querySelector('[data-3d-card]');
    this.glare = this.section.querySelector('[data-glare]');
    this.floatingBadges = this.section.querySelectorAll('.gr-floating-badge');
    
    this.isHovered = false;
    this.mouse = { x: 0, y: 0, targetX: 0, targetY: 0 };
    this.animationFrameId = null;
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.init();
  }

  init() {
    this.initScrollReveal();
    this.init3DTilt();
  }

  /**
   * Scroll Reveal IntersectionObserver
   */
  initScrollReveal() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.section.classList.add('is-in-view');
          observer.unobserve(this.section);
        }
      });
    }, { threshold: 0.15 });

    observer.observe(this.section);
  }

  /**
   * 3D Gyroscopic Tilt & Dynamic Specular Glare Tracking
   */
  init3DTilt() {
    if (!this.cardStage || !this.card || this.prefersReducedMotion) return;

    const stage = this.cardStage;
    const card = this.card;

    stage.addEventListener('mouseenter', () => {
      this.isHovered = true;
      if (this.glare) {
        this.glare.style.setProperty('--glare-opacity', '1');
      }
      this.startLoop();
    });

    stage.addEventListener('mousemove', (e) => {
      const rect = stage.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width; // 0 to 1
      const y = (e.clientY - rect.top) / rect.height; // 0 to 1

      // Normalized coordinates: -1 to 1
      const factor = this.isRTL ? -1 : 1;
      this.mouse.targetX = (x * 2 - 1) * factor;
      this.mouse.targetY = y * 2 - 1;

      // Update Specular Glare Coordinates
      if (this.glare) {
        this.glare.style.setProperty('--glare-x', `${(x * 100).toFixed(1)}%`);
        this.glare.style.setProperty('--glare-y', `${(y * 100).toFixed(1)}%`);
      }
    });

    stage.addEventListener('mouseleave', () => {
      this.isHovered = false;
      this.mouse.targetX = 0;
      this.mouse.targetY = 0;
      if (this.glare) {
        this.glare.style.setProperty('--glare-opacity', '0');
      }
    });
  }

  startLoop() {
    if (this.animationFrameId) return;

    const render = () => {
      // Smooth Lerp Damping
      this.mouse.x += (this.mouse.targetX - this.mouse.x) * 0.12;
      this.mouse.y += (this.mouse.targetY - this.mouse.y) * 0.12;

      const maxTilt = 14; // Max tilt degrees
      const rotY = this.mouse.x * maxTilt;
      const rotX = this.mouse.y * -maxTilt;

      // Apply 3D Rotation to Card
      this.card.style.transform = `
        rotateX(${rotX.toFixed(2)}deg) 
        rotateY(${rotY.toFixed(2)}deg)
        translateZ(${this.isHovered ? 20 : 0}px)
      `;

      // Parallax Lift on Floating Badges
      this.floatingBadges.forEach(badge => {
        const zLift = parseFloat(badge.getAttribute('data-z-layer')) || 40;
        const badgeX = this.mouse.x * 6;
        const badgeY = this.mouse.y * 6;
        badge.style.transform = `
          translate3d(${badgeX.toFixed(1)}px, ${badgeY.toFixed(1)}px, ${zLift}px)
        `;
      });

      // Continue loop if active or smoothly settling back to rest
      if (this.isHovered || Math.abs(this.mouse.x) > 0.001 || Math.abs(this.mouse.y) > 0.001) {
        this.animationFrameId = requestAnimationFrame(render);
      } else {
        this.card.style.transform = 'rotateX(0deg) rotateY(0deg) translateZ(0px)';
        this.floatingBadges.forEach(badge => {
          const zLift = parseFloat(badge.getAttribute('data-z-layer')) || 40;
          badge.style.transform = `translate3d(0px, 0px, ${zLift}px)`;
        });
        cancelAnimationFrame(this.animationFrameId);
        this.animationFrameId = null;
      }
    };

    this.animationFrameId = requestAnimationFrame(render);
  }
}

// Auto-initialize
document.addEventListener('DOMContentLoaded', () => {
  const sections = document.querySelectorAll('.gr-founder-section');
  sections.forEach((section) => {
    new FounderStory3DCard(section);
  });
});

// Shopify Theme Editor Integration
if (window.Shopify && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('.gr-founder-section');
    if (section) {
      new FounderStory3DCard(section);
    }
  });
}
